/**
 * SB6-II-01 — Edit Inspection Instruction modal.
 *
 * Read-only header (inspection_code, inspection_instruction_id,
 * created/updated_*) — and deleted_by/deleted_at shown only when state ∈
 * {WITHDRAWN, REJECTED}. Editable: notes (live "{n} of {max}" counter),
 * external ids (Site-modal pattern), ai flags. Re-link via the shared
 * Project → Contract → Contract Position cascade (PM / TA only). Buttons:
 * Save; a state-driven Withdraw([CREATED,SCHEDULED]) / Reject(the 4 mid
 * states) calling /transition; and a Reactivate button on terminal
 * states that shows a "This routine is not implemented yet" popup.
 *
 * Permission note: the re-link + Withdraw/Reject gate uses
 * `"inspection_instruction.update_state"` (the existing II matrix key —
 * conditional-true for PM/TA). There is no dedicated re-link key; the BE
 * remains authoritative.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../account/ToastContext.js";
import { ExternalIdsField } from "../sites/shared/ExternalIdsField.js";
import {
  ProjectContractPositionCascade,
  type CascadeValue,
} from "./ProjectContractPositionCascade.js";
import { useInspectionInstructionDetailQuery } from "./useInspectionInstructions.js";
import {
  usePatchInspectionInstructionMutation,
  useTransitionInspectionInstructionMutation,
} from "./mutations.js";
import {
  II_NOTES_MAX,
  II_REJECTABLE_STATES,
  II_TERMINAL_STATES,
  II_WITHDRAWABLE_STATES,
  type IiDetail,
  type IiExternalId,
  type IiUserRef,
  type InspectionInstructionState,
} from "./types.js";

interface EditFormValues {
  aiFindingRequired: boolean;
  aiComponentSegmentationRequired: boolean;
  notes: string;
  externalIds: IiExternalId[];
}

function detailToForm(d: IiDetail): EditFormValues {
  return {
    aiFindingRequired: d.aiFindingRequired ?? false,
    aiComponentSegmentationRequired: d.aiComponentSegmentationRequired ?? false,
    notes: d.notes ?? "",
    externalIds: d.externalIds,
  };
}

function dropEmptyExternalIds(arr: IiExternalId[]): IiExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function isWithdrawable(state: InspectionInstructionState): boolean {
  return (II_WITHDRAWABLE_STATES as readonly string[]).includes(state);
}
function isRejectable(state: InspectionInstructionState): boolean {
  return (II_REJECTABLE_STATES as readonly string[]).includes(state);
}
function isTerminal(state: InspectionInstructionState): boolean {
  return (II_TERMINAL_STATES as readonly string[]).includes(state);
}

const NOT_IMPLEMENTED_COPY = "This routine is not implemented yet";

export interface EditInspectionInstructionDialogProps {
  inspectionInstructionId: string | null;
  onClose: () => void;
}

export function EditInspectionInstructionDialog({
  inspectionInstructionId,
  onClose,
}: EditInspectionInstructionDialogProps): ReactElement {
  const open =
    typeof inspectionInstructionId === "string" &&
    inspectionInstructionId.length > 0;
  const {
    data: detail,
    isPending,
    isError,
  } = useInspectionInstructionDetailQuery(inspectionInstructionId);
  const { toast } = useToast();

  // Re-link + transition gate (PM / TA via the existing II matrix key).
  const canRelink = useCanEditResourceFor(
    "inspection_instruction.update_state",
  );

  const patch = usePatchInspectionInstructionMutation();
  const transition = useTransitionInspectionInstructionMutation();

  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [cascade, setCascade] = useState<CascadeValue>({
    projectId: null,
    contractId: null,
    contractPositionId: null,
  });

  const { register, handleSubmit, reset, watch, control } =
    useForm<EditFormValues>({
      defaultValues: detail
        ? detailToForm(detail)
        : {
            aiFindingRequired: false,
            aiComponentSegmentationRequired: false,
            notes: "",
            externalIds: [],
          },
    });

  useEffect(() => {
    if (detail) {
      reset(detailToForm(detail));
      setCascade({
        projectId: detail.projectId,
        contractId: detail.contractId,
        contractPositionId: detail.contractPositionId,
      });
    }
  }, [detail, reset]);

  const notes = watch("notes");
  const aiFindingRequired = watch("aiFindingRequired");
  const aiComponentSegmentationRequired = watch(
    "aiComponentSegmentationRequired",
  );

  const counter = useMemo(
    () => `${String(notes.length)} of ${String(II_NOTES_MAX)}`,
    [notes],
  );

  function onSubmit(v: EditFormValues): void {
    if (!detail) return;
    const body: Parameters<typeof patch.mutate>[0]["body"] = {
      aiFindingRequired: v.aiFindingRequired,
      aiComponentSegmentationRequired: v.aiComponentSegmentationRequired,
      notes: v.notes,
      externalIds: dropEmptyExternalIds(v.externalIds),
    };
    // Re-link only when PM/TA changed the position to a new one.
    if (
      canRelink &&
      cascade.contractPositionId !== null &&
      cascade.contractPositionId !== detail.contractPositionId
    ) {
      body.contractPositionId = cascade.contractPositionId;
    }
    patch.mutate(
      { id: detail.inspectionInstructionId, body },
      {
        onSuccess: () => {
          toast("Inspection instruction updated.");
          onClose();
        },
        onError: () => {
          toast("Couldn't save changes. Try again.", "error");
        },
      },
    );
  }

  function doTransition(action: "WITHDRAW" | "REJECT"): void {
    if (!detail) return;
    transition.mutate(
      { id: detail.inspectionInstructionId, body: { action } },
      {
        onSuccess: () => {
          toast(action === "WITHDRAW" ? "Withdrawn." : "Rejected.");
          onClose();
        },
        onError: () => {
          toast("Couldn't change state. Try again.", "error");
        },
      },
    );
  }

  const state = detail?.state ?? null;
  const showWithdraw = state !== null && isWithdrawable(state);
  const showReject = state !== null && isRejectable(state);
  const showReactivate = state !== null && isTerminal(state);
  const showDeleted = state === "WITHDRAWN" || state === "REJECTED";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-ii-heading"
      >
        <DialogTitle id="edit-ii-heading">
          {detail
            ? `Inspection Instruction ${String(detail.instructionCode)}`
            : "Inspection Instruction"}
        </DialogTitle>
        <DialogContent>
          {isPending ? (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={48} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert severity="error">
              Couldn&apos;t load this inspection instruction.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {/* Identity — permanently read-only. */}
              <Typography variant="overline" color="text.secondary">
                Identity
              </Typography>
              <TextField
                label="Inspection code"
                value={String(detail.instructionCode)}
                variant="standard"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Inspection code",
                    readOnly: true,
                    tabIndex: -1,
                  },
                }}
                fullWidth
              />
              <TextField
                label="Inspection instruction ID"
                value={detail.inspectionInstructionId}
                variant="standard"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Inspection instruction ID",
                    readOnly: true,
                    tabIndex: -1,
                  },
                }}
                fullWidth
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                Link
              </Typography>
              {/* Re-link cascade — PM/TA only; disabled otherwise. */}
              <ProjectContractPositionCascade
                value={cascade}
                onChange={setCascade}
                enabled={open}
                disabled={!canRelink}
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                Details
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register("aiFindingRequired")}
                    checked={aiFindingRequired}
                  />
                }
                label="AI finding required"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    {...register("aiComponentSegmentationRequired")}
                    checked={aiComponentSegmentationRequired}
                  />
                }
                label="AI component segmentation required"
              />
              <TextField
                {...register("notes", { maxLength: II_NOTES_MAX })}
                label="Notes"
                multiline
                rows={3}
                helperText={counter}
                slotProps={{
                  htmlInput: {
                    "aria-label": "Notes",
                    maxLength: II_NOTES_MAX,
                  },
                }}
                fullWidth
              />
              <ExternalIdsField control={control} register={register} />

              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                Audit
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <AuditField label="Created at" value={detail.createdAt} />
                <AuditField
                  label="Created by"
                  value={composeUserRef(detail.createdBy)}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <AuditField label="Updated at" value={detail.updatedAt} />
                <AuditField
                  label="Updated by"
                  value={composeUserRef(detail.updatedBy)}
                />
              </Stack>
              {showDeleted ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <AuditField label="Deleted at" value={detail.deletedAt} />
                  <AuditField
                    label="Deleted by"
                    value={composeUserRef(detail.deletedBy)}
                  />
                </Stack>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={patch.isPending}>
            Cancel
          </Button>
          {showReactivate ? (
            <Button
              onClick={() => {
                setReactivateOpen(true);
              }}
            >
              Reactivate
            </Button>
          ) : null}
          {canRelink && showWithdraw ? (
            <Button
              color="error"
              onClick={() => {
                doTransition("WITHDRAW");
              }}
              disabled={transition.isPending}
            >
              Withdraw
            </Button>
          ) : null}
          {canRelink && showReject ? (
            <Button
              color="error"
              onClick={() => {
                doTransition("REJECT");
              }}
              disabled={transition.isPending}
            >
              Reject
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {!showReactivate ? (
            <Button
              variant="contained"
              onClick={() => {
                void handleSubmit(onSubmit)();
              }}
              disabled={!detail || patch.isPending}
              startIcon={
                patch.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* Reactivate not-implemented popup (AC15). */}
      <Dialog
        open={reactivateOpen}
        onClose={() => {
          setReactivateOpen(false);
        }}
        aria-labelledby="reactivate-not-impl-heading"
      >
        <DialogTitle id="reactivate-not-impl-heading">Reactivate</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{NOT_IMPLEMENTED_COPY}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReactivateOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Compose a created/updated/deleted-by user ref into `{first} {last} (tenant)`.
 *  Returns an em-dash for a null ref or an empty (unresolved) ref — never the
 *  raw object (which used to render as "[object Object]"). */
function composeUserRef(ref: IiUserRef | null): string {
  if (!ref) return "—";
  const name = `${ref.firstName ?? ""} ${ref.lastName ?? ""}`.trim();
  if (!name) return "—";
  return ref.tenantName ? `${name} (${ref.tenantName})` : name;
}

function AuditField({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): ReactElement {
  return (
    <TextField
      label={label}
      value={value ?? "Unknown"}
      variant="standard"
      slotProps={{
        htmlInput: { "aria-label": label, readOnly: true, tabIndex: -1 },
        ...(value === null ? { input: { sx: { fontStyle: "italic" } } } : {}),
      }}
      fullWidth
    />
  );
}
